import { Report, RichReport, Severity, sortReportsByAgentOrder } from './data';

type SnapshotAgent = {
  id: string;
  label: string;
  severity: Severity;
  attention?: string;
  action?: string;
  headline: string;
  hero: { value: string; label: string; sub?: string };
  kpis?: { label: string; value: string; delta?: number }[];
  series?: number[];
  body?: string;
  source?: { name?: string; at?: string; confidence?: 'high' | 'medium' | 'low' };
  stats?: Record<string, any>;
};

export type LiveSnapshot = {
  generatedAt: string;
  summary: { totalAgents: number; critical: number; watch: number; fyi: number };
  agents: SnapshotAgent[];
};

const idMap: Record<string, string> = {
  'shopify-retail': 'retail',
  'shopify-wholesale': 'wholesale',
  'fishbowl-commerce': 'fishbowl',
  markettime: 'markettime',
  quickbooks: 'quickbooks',
};

function plainSummary(body = '') {
  return body
    .replace(/^###\s+/gm, '')
    .replace(/^[-*]\s+/gm, '')
    .replace(/\*\*/g, '')
    .replace(/\n{2,}/g, ' ')
    .trim();
}

function currencyValue(value = '') {
  const parsed = Number(value.replace(/[^0-9.-]/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatMoney(value: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
}

function formatCompactMoney(value: number) {
  if (!Number.isFinite(value)) return 'Unavailable';
  if (Math.abs(value) < 1000) return formatMoney(value);
  const digits = Math.abs(value) >= 100_000 ? 0 : 1;
  return `$${(value / 1000).toFixed(digits).replace(/\.0$/, '')}k`;
}

function compactMoneyText(value = '') {
  const parsed = currencyValue(value);
  return parsed ? formatCompactMoney(parsed) : value;
}

function laneName(report: Report) {
  return report.agentId === 'retail'
    ? 'Retail'
    : report.agentId === 'wholesale'
      ? 'Wholesale'
      : report.agentId === 'fishbowl'
        ? 'Fishbowl'
        : report.agentId === 'markettime'
          ? 'MarketTime'
        : report.agentId === 'quickbooks'
          ? 'QuickBooks'
          : 'Blonde';
}

function number(value: unknown) {
  return Number(value || 0);
}

function formatCount(value: unknown) {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(number(value));
}

function buildRichReport(agent: SnapshotAgent): RichReport | undefined {
  const stats = agent.stats ?? {};
  if (agent.id === 'shopify-retail' || agent.id === 'shopify-wholesale') {
    const today = stats.periods?.today ?? {};
    const month = stats.periods?.monthToDate ?? {};
    const prior = stats.periods?.priorMonthSameDays ?? {};
    const delta = number(prior.netSales) ? ((number(month.netSales) - number(prior.netSales)) / number(prior.netSales)) * 100 : 0;
    const top = (stats.topProducts ?? []).slice(0, 4).map((item: any) => ({ label: item.product ?? item.name ?? item.title ?? 'Product', value: formatCount(item.units ?? item.quantity ?? item.netQuantity ?? item.total ?? 0) }));
    const stock = (stats.inventorySummary?.atRisk ?? []).slice(0, 4).map((item: any) => ({ label: item.product ?? item.sku ?? 'Item', note: item.variant ?? item.category ?? 'Stock watch' }));
    const discounts = (stats.discounts?.monthToDate ?? []).slice(0, 3).map((item: any) => ({ label: item.name ?? 'Discount', value: formatMoney(number(item.amount)), note: `${formatCount(item.orders)} orders` }));
    return {
      theme: 'commerce', title: agent.id === 'shopify-retail' ? 'RETAIL DAYBOOK' : 'WHOLESALE DAYBOOK',
      metrics: [
        { label: 'Net sales', value: formatMoney(number(today.netSales)) }, { label: 'Orders', value: formatCount(today.orders) },
        { label: 'AOV', value: formatMoney(number(today.averageOrderValue)) }, { label: 'Units', value: formatCount(today.units) },
      ],
      feature: { label: 'Month to date', value: formatMoney(number(month.netSales)), sub: `${delta >= 0 ? '+' : ''}${delta.toFixed(1)}% vs same days last month` },
      lists: [
        { title: agent.id === 'shopify-retail' ? 'Top products' : 'Top movers', items: top.length ? top : [{ label: 'No product ranking available' }] },
        { title: agent.id === 'shopify-retail' ? 'Online stockouts' : 'Out on wholesale', tone: 'rose', items: stock.length ? stock : [{ label: 'No stockouts reported' }] },
        ...(discounts.length ? [{ title: agent.id === 'shopify-retail' ? 'Discount watch' : 'Reorder watch', tone: 'sage' as const, items: discounts }] : []),
      ],
      note: agent.action,
    };
  }
  if (agent.id === 'fishbowl-commerce') {
    const counts = stats.counts ?? {};
    const actions = (stats.todayActions ?? []).slice(0, 5).map((item: any) => ({ label: item.partNumber ?? item.part ?? 'Part', value: item.action ?? 'Review', note: item.description ?? item.reason ?? '' }));
    const blockers = (stats.productionBlockers ?? []).slice(0, 4).map((item: any) => ({ label: item.partNumber ?? item.part ?? 'Component', value: item.missing ? formatCount(item.missing) : undefined, note: item.description ?? 'Production blocker' }));
    return {
      theme: 'inventory', title: 'ACTION REPORT',
      metrics: [
        { label: 'Inventory', value: formatCount(counts.inventory) }, { label: 'Low stock', value: formatCount(counts.lowStock) },
        { label: 'Open POs', value: formatCount(counts.openPurchaseOrders) }, { label: 'Open MOs', value: formatCount(counts.openManufactureOrders) },
        { label: 'Blocked', value: formatCount(counts.blockedBuilds) }, { label: 'Buildable', value: formatCount(counts.buildableMos) },
      ],
      lists: [
        { title: "Today's ranked actions", items: actions.length ? actions : [{ label: 'No ranked actions' }] },
        { title: 'Production blockers', tone: 'rose', items: blockers.length ? blockers : [{ label: 'No blockers reported' }] },
      ], note: agent.action,
    };
  }
  if (agent.id === 'quickbooks') {
    const balances = (stats.balances ?? []).slice(0, 6).map((item: any) => ({ label: item.name ?? 'Account', value: formatMoney(number(item.value)) }));
    return {
      theme: 'finance', title: 'OWNER FINANCE REPORT',
      metrics: [
        { label: 'Income', value: formatCompactMoney(number(stats.income)) }, { label: 'Gross profit', value: formatCompactMoney(number(stats.grossProfit)) },
        { label: 'Expenses', value: formatCompactMoney(number(stats.expenses)) }, { label: 'Net income', value: formatCompactMoney(number(stats.netIncome)) },
      ],
      feature: { label: 'Bank cash', value: formatMoney(number(stats.cash)), sub: `${number(stats.grossMargin).toFixed(1)}% gross margin · ${number(stats.netMargin).toFixed(1)}% net margin` },
      lists: [{ title: 'Cash + balance sheet', items: balances.length ? balances : [{ label: 'No account balances available' }] }, { title: 'Owner focus', tone: 'sage', items: [{ label: agent.attention || agent.headline }, { label: 'Next action', note: agent.action }] }],
      note: agent.action,
    };
  }
  if (agent.id === 'markettime') {
    const reps = (stats.topReps ?? []).slice(0, 4).map((item: any) => ({ label: item.name ?? 'Rep', value: item.sales ?? 'Unavailable' }));
    const products = (stats.topProducts ?? []).slice(0, 4).map((item: any) => ({ label: item.name ?? 'Product', value: item.sales ?? 'Unavailable' }));
    const followUps = (stats.followUps ?? []).slice(0, 4).map((item: any) => ({ label: item.account ?? 'Account', value: item.sales ?? undefined, note: item.lastOrder ?? 'Follow up' }));
    return {
      theme: 'markettime', title: 'MARKETTIME SALES REPORT',
      metrics: [
        { label: 'Today', value: stats.todaySales ?? agent.hero.value },
        { label: 'September MTD', value: stats.mtdSales ?? 'Unavailable' },
        { label: '2026 YTD', value: stats.ytdSales ?? 'Unavailable' },
        { label: 'Orders pulled', value: formatCount(stats.ordersPulled) },
      ],
      feature: { label: 'Wholesale momentum', value: stats.ytdGrowth ?? 'Unavailable', sub: 'year to date versus comparable prior year' },
      lists: [
        { title: 'Rep leaderboard', items: reps.length ? reps : [{ label: 'No rep ranking available' }] },
        { title: 'Top products', tone: 'sage', items: products.length ? products : [{ label: 'No product ranking available' }] },
        { title: 'Accounts to follow up', tone: 'rose', items: followUps.length ? followUps : [{ label: 'No overdue accounts reported' }] },
      ],
      note: agent.action,
    };
  }
  return undefined;
}

export function snapshotToReports(snapshot: LiveSnapshot): Report[] {
  const lanes = snapshot.agents.map((agent): Report => ({
    id: `live-${agent.id}`,
    agentId: idMap[agent.id] ?? agent.id,
    time: agent.source?.at
      ? new Date(agent.source.at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
      : 'Latest',
    headline: agent.headline,
    severity: agent.severity,
    attention: agent.attention ?? '',
    action: agent.action ?? 'Review this report with the lane owner.',
    hero: { value: agent.hero.value, label: agent.hero.label, sub: agent.hero.sub ?? '' },
    kpis: (agent.kpis ?? []).slice(0, 3),
    series: agent.series?.slice(-7),
    source: agent.source?.name ?? `${agent.label} live report`,
    confidence: agent.source?.confidence ?? 'medium',
    summary: plainSummary(agent.body) || agent.headline,
    rich: buildRichReport(agent),
  }));

  const retail = lanes.find((report) => report.agentId === 'retail');
  const wholesale = lanes.find((report) => report.agentId === 'wholesale');
  const quickbooks = lanes.find((report) => report.agentId === 'quickbooks');
  const critical = lanes.filter((report) => report.severity === 'critical');
  const watch = lanes.filter((report) => report.severity === 'watch');
  const combinedSales = currencyValue(retail?.hero.value) + currencyValue(wholesale?.hero.value);
  const cash = quickbooks?.kpis.find((kpi) => /cash/i.test(kpi.label))?.value ?? 'Unavailable';
  const ownerSeverity: Severity = critical.length ? 'critical' : watch.length ? 'watch' : 'fyi';
  const focus = critical.length ? critical : watch;
  const focusNames = focus.map(laneName);
  const focusLabel = focusNames.length > 1
    ? `${focusNames.slice(0, -1).join(', ')} and ${focusNames.at(-1)}`
    : focusNames[0];
  const ownerHeadline = focus.length
    ? `Sales are ${formatCompactMoney(combinedSales)} today, but ${focusLabel} ${focus.length === 1 ? 'needs' : 'need'} attention`
    : `Sales are ${formatCompactMoney(combinedSales)} today and all five reporting lanes look clear`;
  const ownerAttention = focus
    .map((report) => report.attention || report.headline)
    .filter(Boolean)
    .slice(0, 2)
    .join(' ');

  const owner: Report = {
    id: 'live-owner',
    agentId: 'owner',
    time: new Date(snapshot.generatedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }),
    severity: ownerSeverity,
    headline: ownerHeadline,
    attention: ownerAttention,
    action: focus[0]?.action ?? 'No owner action is required right now.',
    hero: {
      value: formatCompactMoney(combinedSales),
      label: 'combined sales today',
      sub: `${snapshot.summary.critical} act today · ${snapshot.summary.watch} watch · ${compactMoneyText(cash)} cash`,
    },
    kpis: [
      { label: 'Retail', value: compactMoneyText(retail?.hero.value) || 'Unavailable' },
      { label: 'Wholesale', value: compactMoneyText(wholesale?.hero.value) || 'Unavailable' },
      { label: 'Cash', value: compactMoneyText(cash) },
    ],
    source: 'Shopify + Fishbowl + MarketTime + QuickBooks',
    confidence: lanes.every((report) => report.confidence === 'high') ? 'high' : 'medium',
    summary: `Thomas Blonde AI cross-referenced ${snapshot.summary.totalAgents} live reporting lanes. ${snapshot.summary.critical} are critical, ${snapshot.summary.watch} are watch items, and ${snapshot.summary.fyi} are normal.`,
  };

  return sortReportsByAgentOrder([owner, ...lanes]);
}
